import { useCallback } from 'react'
import { useDispatch } from 'react-redux'
import { BulkUploadPractice } from 'src/redux/actions'
import { buildSampleRows, describeError } from 'src/util/bulkImport'
import { downloadCsv } from 'src/util/csv'

import BulkUploadPanel from '../helperComponents/BulkUploadPanel'
import { PRACTICE_ITEM_CSV_HEADERS } from './BulkUploadPracticeItem'

const PRACTICE_SET_HEADERS = ['language', 'course', 'chapter', 'lesson', 'practiceList', 'practiceListStatus']

const ITEM_NAME_COLUMN = 'practiceItem'
const ITEM_COLUMNS = PRACTICE_ITEM_CSV_HEADERS.filter(header => header !== 'name')

export const LESSON_PRACTICE_CSV_HEADERS = [
  ...PRACTICE_SET_HEADERS,
  ITEM_NAME_COLUMN,
  ...ITEM_COLUMNS,
]

// Every row shares one lesson unless it says otherwise, so the sample reads as
// two real Practice Lists rather than a column reference.
const IN_LESSON = {
  language: 'English',
  course: 'Basics of Greetings',
  chapter: 'Greetings Basics',
  lesson: 'Greeting Someone New',
  practiceList: 'Greetings Practice Set',
  practiceListStatus: 'Active',
  status: 'Active',
  coin: '5',
}

/**
 * One row per Question Type, each filling exactly the columns its type reads —
 * so the file doubles as the reference for which columns belong to which type.
 * WATCH 4LVIDEO is intentionally absent: the Practice Item form has no fields
 * for it and the API rejects it.
 */
const SAMPLE_LESSON_PRACTICE = [
  {
    ...IN_LESSON,
    practiceItem: 'Listen and choose the correct image - Apple',
    questionType: 'LISTEN WITH IMAGE',
    category: 'Listening',
    question: 'Listen to the audio and choose the correct image.',
    audioUrl: 'apple_audio.mp3',
    audioSlowUrl: 'apple_audio_slow.mp3',
    answer: 'option1',
    option1Url: 'apple.jpg',
    option2Url: 'banana.jpg',
    option3Url: 'cherry.jpg',
  },
  {
    ...IN_LESSON,
    practiceItem: 'Listen and choose the right audio - Greeting',
    questionType: 'LISTEN WITH AUDIO',
    category: 'Listening',
    question: 'Listen to the prompt and choose the matching audio.',
    isImage: 'Yes',
    exerciseImageUrl: 'greeting_prompt.jpg',
    audioUrl: 'greeting_audio.mp3',
    audioSlowUrl: 'greeting_audio_slow.mp3',
    answer: 'option2',
    option1Url: 'greeting_option1.mp3',
    option2Url: 'greeting_option2.mp3',
  },
  {
    ...IN_LESSON,
    practiceItem: 'Listen and choose the right text - Hello',
    questionType: 'LISTEN WITH TEXT',
    category: 'Reading',
    question: 'Listen to the audio and choose the correct text.',
    isImage: 'No',
    exerciseVideoUrl: 'hello_prompt.mp4',
    audioUrl: 'hello_audio.mp3',
    audioSlowUrl: '',
    answer: 'option1',
    option1Text: 'Hello',
    nativeOption1Text: 'Namaste',
    option2Text: 'Goodbye',
    nativeOption2Text: 'Alvida',
  },
  {
    ...IN_LESSON,
    practiceItem: 'Listen and put the words in order - Good morning everyone',
    questionType: 'LISTEN WITH ORDER',
    category: 'Reading',
    question: 'Listen and arrange the words in the correct order.',
    isImage: 'No',
    exerciseVideoUrl: '',
    audioUrl: 'good_morning_everyone.mp3',
    audioSlowUrl: 'good_morning_everyone_slow.mp3',
    // For the order types the answer is the finished sentence the option words spell.
    answer: 'Good morning everyone',
    option1Text: 'Good',
    option1Url: 'good.mp3',
    option2Text: 'morning',
    option2Url: 'morning.mp3',
    option3Text: 'everyone',
    option3Url: 'everyone.mp3',
  },
  {
    ...IN_LESSON,
    practiceItem: 'Listen and type what you hear - Good morning',
    questionType: 'LISTEN WITH TYPE',
    category: 'Writing',
    question: 'Listen to the audio and type what you hear.',
    audioUrl: 'good_morning_audio.mp3',
    audioSlowUrl: '',
    answer: 'Good morning',
  },
  {
    ...IN_LESSON,
    practiceItem: 'Listen and record yourself - Thank you',
    questionType: 'LISTEN WITH RECORD',
    category: 'Speaking',
    question: 'Listen to the audio and record yourself repeating it.',
    audioUrl: 'thank_you_audio.mp3',
    audioSlowUrl: '',
    answer: 'Thank you',
  },
  {
    ...IN_LESSON,
    lesson: 'Saying Goodbye',
    practiceList: 'Goodbye Practice Set',
    practiceItem: 'Watch and choose the right audio - Farewell',
    questionType: 'WATCH WITH AUDIO',
    category: 'Listening',
    question: 'Watch the video and choose the matching audio.',
    coin: '10',
    isImage: 'No',
    exerciseVideoUrl: '',
    // videoUrl is required for WATCH WITH AUDIO and WATCH WITH ORDER.
    videoUrl: 'https://cdn.example.com/media/farewell_video.mp4',
    answer: 'option1',
    option1Url: 'farewell_option1.mp3',
    option2Url: 'farewell_option2.mp3',
  },
  {
    ...IN_LESSON,
    lesson: 'Saying Goodbye',
    practiceList: 'Goodbye Practice Set',
    practiceItem: 'Watch and choose the right text - Goodbye',
    questionType: 'WATCH WITH TEXT',
    category: 'Reading',
    question: 'Watch the video and choose the correct word.',
    coin: '10',
    isImage: 'No',
    exerciseVideoUrl: '',
    videoUrl: 'https://cdn.example.com/media/goodbye_video.mp4',
    answer: 'option1',
    option1Text: 'Goodbye',
    nativeOption1Text: 'Alvida',
    option2Text: 'Hello',
    nativeOption2Text: 'Namaste',
  },
  {
    ...IN_LESSON,
    lesson: 'Saying Goodbye',
    practiceList: 'Goodbye Practice Set',
    practiceItem: 'Watch and put the words in order - See you tomorrow',
    questionType: 'WATCH WITH ORDER',
    category: 'Reading',
    question: 'Watch the video and arrange the words in order.',
    coin: '10',
    isImage: 'No',
    exerciseVideoUrl: '',
    videoUrl: 'https://cdn.example.com/media/see_you_tomorrow.mp4',
    answer: 'See you tomorrow',
    option1Text: 'See',
    option2Text: 'you',
    option3Text: 'tomorrow',
  },
  {
    ...IN_LESSON,
    lesson: 'Saying Goodbye',
    practiceList: 'Goodbye Practice Set',
    practiceItem: 'Watch and type what you hear - Take care',
    questionType: 'WATCH WITH TYPE',
    category: 'Writing',
    question: 'Watch the video and type what you hear.',
    coin: '10',
    isImage: 'No',
    exerciseVideoUrl: '',
    videoUrl: 'https://cdn.example.com/media/take_care_video.mp4',
    answer: 'Take care',
  },
  {
    ...IN_LESSON,
    lesson: 'Saying Goodbye',
    practiceList: 'Goodbye Practice Set',
    practiceItem: 'Watch and record yourself - See you soon',
    questionType: 'WATCH WITH RECORD',
    category: 'Speaking',
    question: 'Watch the video and record yourself repeating it.',
    coin: '10',
    isImage: 'Yes',
    exerciseImageUrl: 'see_you_soon_prompt.jpg',
    videoUrl: 'https://cdn.example.com/media/see_you_soon_video.mp4',
    answer: 'See you soon',
  },
  {
    ...IN_LESSON,
    lesson: 'Saying Goodbye',
    practiceList: 'Goodbye Practice Set',
    practiceItem: 'Watch the farewell video',
    questionType: 'WATCH VIDEO',
    category: 'Listening',
    question: 'Watch the video all the way through.',
    coin: '10',
    // WATCH VIDEO is the one type that takes no answer.
    exerciseVideoUrl: 'farewell_full_video.mp4',
  },
  {
    // A set on its own row with no practiceItem creates an empty Practice List.
    language: 'English',
    course: 'Numbers and Counting',
    chapter: 'Numbers 1 to 20',
    lesson: 'Formal Introductions',
    practiceList: 'Numbers Practice Set',
    practiceListStatus: 'Inactive',
  },
]

export const SAMPLE_LESSON_PRACTICE_CSV_ROWS = buildSampleRows(
  LESSON_PRACTICE_CSV_HEADERS,
  SAMPLE_LESSON_PRACTICE
)

export const downloadSampleLessonPracticeCsv = () =>
  downloadCsv('bulk-lesson-practice-sample.csv', SAMPLE_LESSON_PRACTICE_CSV_ROWS)

// Display only — how many sets the file describes, so a rejected upload can say
// "0 of 4" instead of "0 of 0". Nothing is judged here; the API decides.
const countPracticeSets = rows =>
  new Set(
    rows
      .map(row => String(row.practiceList ?? '').trim().toLowerCase())
      .filter(Boolean)
  ).size

const describeRow = entry => (entry.row ? `Row ${entry.row}` : 'This file')

// Both errors and warnings arrive as { row, field, message }.
const toPanelEntry = entry => ({
  label: describeRow(entry),
  reason: entry.field ? `${entry.field} — ${entry.message}` : entry.message,
})

const BulkUploadLessonPractice = ({ toggleDrawer, refetch }) => {
  const dispatch = useDispatch()

  /**
   * The panel parses the file and hands the rows over; every rule — duplicate
   * names, question types, required fields — is applied by the API, which
   * either creates the whole file or none of it.
   */
  const onImport = useCallback(
    async (rows, { onProgress } = {}) => {
      onProgress?.(`Sending ${rows.length} row${rows.length === 1 ? '' : 's'} for validation…`)

      try {
        const created = (await dispatch(BulkUploadPractice(rows))) || {}
        const lists = created.practiceListsCreated ?? 0
        const items = created.practiceItemsCreated ?? 0

        return {
          total: lists,
          successCount: lists,
          failures: [],
          // Rows that were imported, but not exactly as written — a column the
          // question type ignores, or media the library does not hold.
          warnings: (created.warnings || []).map(toPanelEntry),
          note: `${items} practice item${items === 1 ? '' : 's'} created.`,
        }
      } catch (error) {
        // 422 carries one entry per problem, each naming the row it came from.
        const rowErrors = error?.response?.data?.errors
        if (Array.isArray(rowErrors) && rowErrors.length) {
          return {
            total: countPracticeSets(rows),
            successCount: 0,
            failures: rowErrors.map(toPanelEntry),
            note: 'Nothing was created — fix these rows and upload the file again.',
          }
        }

        // Anything else (network, session, an unexpected server fault) belongs
        // in the form-level error, not against a row.
        throw new Error(describeError(error, 'The upload could not be completed'))
      }
    },
    [dispatch]
  )

  return (
    <BulkUploadPanel
      entityPlural="Practice Lists"
      headers={LESSON_PRACTICE_CSV_HEADERS}
      onDownloadSample={downloadSampleLessonPracticeCsv}
      onImport={onImport}
      toggleDrawer={toggleDrawer}
      refetch={refetch}
      allOrNothing
      helpText={
        'One row per Practice Item. Repeat the language, course, chapter, lesson and practiceList ' +
        'on every row of the same set — those rows become one Practice List, with its items ' +
        'attached in row order. The lesson must already exist, and a Practice List cannot span ' +
        'more than one lesson. practiceList is the list name and practiceItem is the item name; ' +
        'leave practiceItem and the other item columns blank on a single row to create an empty ' +
        'list. Names must be 5 to 75 characters and must not already be in use by a record that ' +
        'has not been deleted. The sample CSV has one row per Question Type, showing exactly ' +
        'which columns each type uses — columns a type does not use are ignored and reported ' +
        'below. Media columns (audioUrl, videoUrl, option1Url, ...) take the stored file name of ' +
        'an already-uploaded file, e.g. "apple_audio.mp3"; a full https:// URL also works and is ' +
        'reduced to the file name. For the option based types the answer must name an option the ' +
        'row actually fills (option1, option2, ...); for the order types it is the finished ' +
        'sentence the option words spell out; WATCH VIDEO takes no answer.'
      }
    />
  )
}

export default BulkUploadLessonPractice
